import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FileText, Users, UserCheck, Shield, AlertTriangle, Loader2 } from 'lucide-react';

interface Document {
  id: string;
  name: string;
  version: string;
}

interface Permissions {
  employee: boolean;
  manager: boolean;
  admin: boolean;
}

interface PermissionsModalProps {
  selectedDocuments: string[];
  documents: Document[];
  onClose: () => void;
}

const PermissionsModal = ({ selectedDocuments, documents, onClose }: PermissionsModalProps) => {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState<Permissions>({
    employee: true,
    manager: true,
    admin: true
  });
  const [isLoading, setIsLoading] = useState(false);

  const selectedDocs = documents?.filter(doc => selectedDocuments?.includes(doc?.id));

  const handlePermissionChange = (level: keyof Permissions, checked: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [level]: checked
    }));
  };

  const handleApply = async () => {
    setIsLoading(true);

    // Mock API call
    setTimeout(() => {
      console.log('Updated permissions for documents:', selectedDocuments);
      console.log('New permissions:', permissions);
      setIsLoading(false);
      onClose();
    }, 1000);
  };

  const getPermissionIcon = (level: keyof Permissions) => {
    const icons = {
      employee: Users,
      manager: UserCheck,
      admin: Shield
    };
    const Icon = icons[level];
    return <Icon className="w-4 h-4 text-gray-500" />;
  };

  const getPermissionDescription = (level: keyof Permissions) => {
    const descriptions = {
      employee: t('All employees can access this document'),
      manager: t('Managers and above can access this document'),
      admin: t('Only administrators can access this document')
    };
    return descriptions[level];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('Manage Permissions')}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('Update access permissions for')} {selectedDocuments?.length} {t('selected document')}{selectedDocuments?.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">

          {/* Selected Documents */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('Selected Documents')}</h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {selectedDocs?.map(doc => (
                <div key={doc?.id} className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900 flex-1 truncate">{doc?.name}</span>
                  <span className="text-xs text-gray-500">v{doc?.version}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Permission Settings */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">{t('Access Permissions')}</h3>
            <div className="space-y-4">

              {(Object.entries(permissions) as [keyof Permissions, boolean][])?.map(([level, checked]) => (
                <div key={level} className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id={level}
                    checked={checked}
                    onChange={(e) => handlePermissionChange(level, e?.target?.checked)}
                    className="mt-1 w-4 h-4 text-primary border-border rounded focus:ring-primary/20"
                  />
                  <div className="flex-1">
                    <label htmlFor={level} className="flex items-center space-x-2 cursor-pointer">
                      {getPermissionIcon(level)}
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {t(level)} {t('Access')}
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      {getPermissionDescription(level)}
                    </p>
                  </div>
                </div>
              ))}

            </div>

            {/* Warning */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">{t('Permission Change Notice')}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    {t('Changing permissions will affect who can view and download these documents. This action will be logged for audit purposes.')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              {t('Cancel')}
            </button>
            <button
              onClick={handleApply}
              disabled={isLoading || !Object.values(permissions)?.some(Boolean)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{isLoading ? t('Updating...') : t('Apply Changes')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsModal;
